import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as actions from '@redux-actions/';

import { base64FileToImage, base64IsImage, formatPhone, getLabelFallback } from '@utils/utils';
import ArrowRight from '@images/svg-icons/arrowRight.svg';
import { IMySavedPrescriptionItem } from '@interfaces/contactsInterfaces';
import LastUpdate from './LastUpdate';
import Modal from '@components/styled-UI/Modal';
import PrescriptionDefaultImage from '@images/svg-icons/prescriptionDefaultImage.svg';
import { downloadPrescriptionFile } from '@services/contactsServices';
import { RootState } from 'src/store/store';

interface Props {
    prescription: IMySavedPrescriptionItem;
}

const SavedPrescriptionCard = ({ prescription }: Props) => {
    const config = useSelector((state: RootState) => state.common.config);
    const dispatch = useDispatch();

    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [prescriptionFile, setPrescriptionFile] = useState(null);
    const [prescriptionImage, setPrescriptionImage] = useState(null);

    const prescriptionFileSize = !!prescriptionFile
        ? ((4 * Math.ceil(prescriptionFile?.length / 3) * 0.5624896334383812) / 1000000).toFixed(2)
        : '';

    useEffect(() => {
        const controller = new AbortController();
        if (!!prescription?.fileName) {
            downloadPrescriptionFile(
                config.mockRequest,
                config.storeId.toString(),
                prescription.fileName,
                controller
            ).then(response => {
                if (!!response?.prescriptionFile) {
                    setPrescriptionFile(response?.prescriptionFile);

                    if (base64IsImage(response.prescriptionFile)) {
                        setPrescriptionImage(base64FileToImage(response.prescriptionFile));
                    }
                }
            });
        }

        return () => controller.abort();
    }, []);

    return (
        <div className="saved-prescription-card">
            <div className="saved-prescription-card-content">
                <div role="region" aria-label="card title" className="card-title">
                    {getLabelFallback('MY_SAVED_PRESCRIPTION_PRESCRIPTION_NAME_LABEL')}: {prescription.firstName} {prescription.lastName}
                    <button
                        className="card-arrow"
                        onClick={() => dispatch(actions.setCurrentPrescription(prescription))}
                        data-analytics_available_call="0"
                    >
                        <ArrowRight />
                    </button>
                </div>

                <div className="presc-upload-date-container">
                    <LastUpdate prescription={prescription} />
                </div>

                <button
                    className="show-presc-btn-content"
                    onClick={() => setIsPreviewModalOpen(true)}
                    data-analytics_available_call="0"
                >
                    {getLabelFallback('CONTACT_LENSES_SHOW_SAVED_PRESCRIPTION')}
                </button>
            </div>

            <Modal isOpen={isPreviewModalOpen} close={() => setIsPreviewModalOpen(false)}>
                <div className="prescription-preview-modal">
                    <div className="prescription-preview-modal_title">
                        {getLabelFallback('YOUR_PRESCRIPTION_TITLE')}
                    </div>

                    <p className="prescription-preview-modal_subtitle">
                        {getLabelFallback('YOUR_PRESCRIPTION_SUBTITLE_PT1')}
                        {getLabelFallback('YOUR_PRESCRIPTION_SUBTITLE_PT2')}
                    </p>

                    <div className="prescription-preview-modal_section">
                        <div className="prescription-preview-modal_section_title">
                            {getLabelFallback('YOUR_DOCTOR_DETAILS')}
                        </div>

                        <div className="doctor-information-table">
                            <div className="doctor-information-table_header-item">
                                {getLabelFallback('DOCTOR_NAME')}
                            </div>
                            <div className="doctor-information-table_body-item">
                                {prescription.doctor.name}
                            </div>

                            <div className="doctor-information-table_header-item">
                                {getLabelFallback('CLINIC_NAME')}
                            </div>
                            <div className="doctor-information-table_body-item">
                                {prescription.doctor.name}
                            </div>

                            <div className="doctor-information-table_header-item">
                                {getLabelFallback('ADDRESS')}
                            </div>
                            <div className="doctor-information-table_body-item">
                                {prescription.doctor.address}
                                {!!prescription.doctor.address && !!prescription.doctor.phone && (
                                    <br />
                                )}
                                {!!prescription.doctor.phone &&
                                    formatPhone(prescription.doctor.phone)}
                            </div>
                        </div>
                    </div>

                    {prescription.submissionMethod === 'uploadPrescription' && (
                        <div className="prescription-preview-modal_section">
                            <div className="prescription-preview-modal_section_title">
                                {getLabelFallback('SUBMISSION_METHOD')}
                            </div>

                            <div className="prescription-file">
                                <div className="prescription-file_image">
                                    {!!prescriptionImage ? (
                                        <img src={prescriptionImage} />
                                    ) : (
                                        <PrescriptionDefaultImage />
                                    )}
                                </div>

                                <div className="prescription-file_data">
                                    {prescription?.fileName} - [{prescriptionFileSize} MB]
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default SavedPrescriptionCard;
